import React, { useEffect, useState, useCallback, useRef, useMemo, memo, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  MapPin, Calendar, Instagram, Share2,
  ChevronDown, ArrowLeft, User2, Heart, Repeat,
  BarChart3, UserPlus, MoreHorizontal, PenLine, Pencil,
} from "lucide-react";
import { eventEditPath } from "../lib/eventLinks";
import { backLabel, defaultBackLabel, defaultBackPath, goBack } from "../lib/navBack";
import FavouriteSeriesPromptDialog from "../components/FavouriteSeriesPromptDialog";
import PushNotificationPromptDialog from "../components/notifications/PushNotificationPromptDialog";
import EventGuestList from "../components/event/EventGuestList";
import GuestListPullToRefresh from "../components/event/GuestListPullToRefresh";
import GuestListRefreshingPopup from "../components/event/GuestListRefreshingPopup";
import EventStickyCta, { getStickyCtaState } from "../components/ui/public/StickyCta";
import DetailPageNav from "../components/ui/public/DetailPageNav";
import DetailHeroCard, { DetailMetaRow } from "../components/ui/public/DetailHero";
import LinkifiedText from "../components/LinkifiedText";
import HostSchoolPromo from "../components/event/HostSchoolPromo";
import { toast } from "sonner";
import { api, ownDancerIdParam } from "../lib/api";
import { loadDancerProfile } from "../lib/dancerProfile";
import { useAuth } from "../lib/auth";
import { useGuestIdentity } from "../lib/guestIdentity";
import { eventMapLink, eventMapQuery, currencySymbol } from "../lib/format";
import { PriceHeroPill } from "../components/ui/public/PricePill";
import { earlyBirdDetailLines } from "../lib/pricing";
import {
  eventStatusColor,
  eventStatusLabel,
} from "../lib/eventStatus";
import {
  attendanceStatus,
  isLikelyComing,
  isTentativeOrDeclined,
  eventRequiresInvite,
} from "../lib/rsvp";
import { canOfferCheckIn } from "../lib/checkIn";
import CheckInButton from "../components/CheckInButton";
import {
  FavoriteDancesSheet,
  fetchFavoriteDances,
  FD_EVENT_CTA,
  FD_VOTE_TITLE,
  isOccurrenceWithinEventVoteRange,
} from "../games/favoriteDances";
import { Button, Badge, CountBadge, Card } from "../components/ui";
import SelfPaymentDisplay from "../components/SelfPaymentDisplay";
import HostCard from "../components/HostCard";
import { hasPaymentMethod } from "../lib/payment";
import { buildEventWhatsAppSummary, openWhatsAppShare } from "../lib/share";
import { instagramHref } from "../lib/instagram";
import { activeCheckInPath, getActiveCheckInForSession, setActiveCheckIn } from "../lib/activeCheckIn";
import { canFollowSeries } from "../lib/eventFollow";
import { isPushPromptDismissed } from "../lib/pushPromptDismiss";
import { shouldOfferPush } from "../lib/webPush";
import AuthModal from "../components/AuthModal";
import { stashPendingAuthAction, consumePendingAuthAction } from "../lib/pendingAuthAction";
import HostThemedShell from "../components/ui/public/HostThemedShell";
import { DEFAULT_HOST_THEME, normalizeHostTheme } from "../lib/hostThemes";
import { listingDrawerBackground } from "../lib/listingDrawer";

const RsvpModal = lazy(() => import("../components/RsvpModal"));

function sortComingRsvps(rsvps) {
  return rsvps
    .filter(isLikelyComing)
    .sort((a, b) => {
      if (a.checked_in_at && !b.checked_in_at) return -1;
      if (!a.checked_in_at && b.checked_in_at) return 1;
      return 0;
    });
}

function eventPollSignature(data) {
  if (!data) return "";
  return JSON.stringify({
    t: data.counts?.total,
    bl: data.counts?.balanced_leaders,
    bf: data.counts?.balanced_followers,
    both: data.counts?.both,
    status: data.event_status,
    rsvps: (data.rsvps || []).map((r) => [
      r.id,
      r.checked_in_at,
      attendanceStatus(r),
      r.first_timer,
      r.role,
      r.hide_name,
    ]),
    inv: data.my_invite_request?.status,
    ir: (data.invite_requests || []).length,
  });
}

function EventPageLoadingSkeleton() {
  return (
    <div className="min-h-screen max-w-page mx-auto px-4 pt-5 pb-6" aria-busy="true" aria-label="Loading event">
      <div className="animate-pulse space-y-5">
        <div className="flex items-center justify-between gap-2">
          <div className="h-11 w-28 rounded-md2 bg-black/[0.06]" />
          <div className="h-11 w-16 rounded-md2 bg-black/[0.06]" />
        </div>
        <div className="h-52 sm:h-60 rounded-card bg-black/[0.06]" />
        <div className="h-7 w-48 rounded bg-black/[0.06]" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[72px] rounded-card bg-black/[0.06]" />
          ))}
        </div>
      </div>
      <p className="sr-only">Loading event…</p>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const EventActionMenu = memo(function EventActionMenu({
  editHref,
  showEditRsvp,
  onEditRsvp,
  showFollow,
  isFavourite,
  onFavourite,
  onShare,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const first = menuRef.current?.querySelector('[role="menuitem"]');
    first?.focus();
  }, [open]);

  function onMenuKey(e) {
    const items = menuRef.current?.querySelectorAll('[role="menuitem"]');
    if (!items?.length) return;
    const list = Array.from(items);
    const index = list.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      list[(index + 1) % list.length]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      list[(index - 1 + list.length) % list.length]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      list[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      list[list.length - 1]?.focus();
    }
  }

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="secondary"
        type="button"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More actions"
        className="px-2.5 w-11"
        data-testid="event-actions-menu-btn"
      >
        <MoreHorizontal className="w-5 h-5" strokeWidth={2.5} aria-hidden />
      </Button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full mt-1 z-30 min-w-[200px] rounded-md2 bg-surface border border-subtle shadow-card py-1 text-ink"
          data-testid="event-actions-menu"
        >
          {editHref ? (
            <Link
              role="menuitem"
              to={editHref}
              onClick={() => setOpen(false)}
              className="w-full text-left px-3 py-2.5 min-h-[44px] font-ui text-[14px] font-medium text-ink hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-offset-[-2px] flex items-center gap-2 whitespace-nowrap"
              data-testid="edit-event-menu-link"
            >
              <Pencil className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
              Edit event
            </Link>
          ) : null}
          {(showFollow || isFavourite) && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { onFavourite(); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 min-h-[44px] font-ui text-[14px] font-medium text-ink hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-offset-[-2px] flex items-center gap-2 whitespace-nowrap"
              data-testid="toggle-favourite-btn"
            >
              <Heart
                className={`w-4 h-4 shrink-0 ${isFavourite ? "fill-ink" : ""}`}
                strokeWidth={2.5}
                aria-hidden
              />
              {isFavourite ? "Unfollow" : "Follow"}
            </button>
          )}
          {showEditRsvp && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { onEditRsvp(); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 min-h-[44px] font-ui text-[14px] font-medium text-ink hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-offset-[-2px] flex items-center gap-2 whitespace-nowrap"
              data-testid="edit-rsvp-btn"
            >
              <PenLine className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
              Edit RSVP
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => { onShare(); setOpen(false); }}
            className="w-full text-left px-3 py-2.5 min-h-[44px] font-ui text-[14px] font-medium text-ink hover:bg-surface-muted focus-visible:bg-surface-muted focus-visible:outline-offset-[-2px] flex items-center gap-2 whitespace-nowrap"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
            WhatsApp
          </button>
        </div>
      )}
    </div>
  );
});

const QUIET_STATUS_BTN =
  "w-full sm:w-auto bg-canvas border border-black/[0.06] rounded-btn px-5 py-3 inline-flex items-center gap-2 shadow-none hover:bg-black/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orchid/45 text-left transition-colors";

const HeroPrimaryCta = memo(function HeroPrimaryCta({
  isCancelled,
  myRsvp,
  myStatus,
  requiresInvite,
  isHosting,
  inviteStatus,
  isFull,
  isPaid,
  isNotConfirmed,
  showCheckIn,
  onOpenForm,
  onOpenInviteForm,
}) {
  if (isCancelled) {
    return (
      <p className="font-ui font-medium text-[14px] text-ink/60">
        RSVPs are closed for this week. Check back for the next one.
      </p>
    );
  }

  if (myRsvp) {
    if (showCheckIn && myStatus === "likely") {
      return null;
    }

    if (myStatus === "not_coming") {
      return (
        <div className="flex flex-wrap gap-3 items-stretch">
          <Button
        variant="primary"
            size="lg"
            onClick={onOpenForm}
            className="inline-flex items-center justify-center"
            data-testid="open-rsvp-btn"
          >
            RSVP →
          </Button>
        </div>
      );
    }

    const firstName = myRsvp.name.split(" ")[0];
    const rsvpLabel = myStatus === "maybe"
      ? `We'll see you if you can, ${firstName}!`
      : `You're in, ${firstName}!`;

    return (
      <div className="flex flex-wrap gap-3 items-stretch">
        <button
          type="button"
          onClick={onOpenForm}
          aria-label="Edit RSVP"
          className={QUIET_STATUS_BTN}
          data-testid="rsvp-confirmed-badge"
        >
          <PenLine className="w-4 h-4 text-ink/50 shrink-0" strokeWidth={2} aria-hidden />
          <span className="font-ui font-medium text-[15px] text-ink">{rsvpLabel}</span>
        </button>
      </div>
    );
  }

  if (requiresInvite && !isHosting) {
    if (inviteStatus === "pending") {
      return (
        <div className="flex flex-wrap gap-3 items-stretch">
          <div className={QUIET_STATUS_BTN} data-testid="invite-pending-badge">
            <UserPlus className="w-4 h-4 text-ink/50 shrink-0" strokeWidth={2} aria-hidden />
            <span className="font-ui font-medium text-[15px] text-ink">Invite requested — waiting for host</span>
          </div>
        </div>
      );
    }
    if (inviteStatus === "rejected") {
      return (
        <div className="flex flex-wrap gap-3 items-stretch">
          <button
            type="button"
            onClick={onOpenInviteForm}
            aria-label="Request invite again"
            className="w-full sm:w-auto bg-night border border-night rounded-btn px-5 py-3 inline-flex items-center gap-2 shadow-none hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orchid/45 text-left transition-[filter]"
            data-testid="request-invite-again-btn"
          >
            <span className="font-ui font-medium text-[15px] text-cream">
              Your invite wasn&apos;t approved this time. Request again →
            </span>
          </button>
        </div>
      );
    }
    if (inviteStatus === "accepted") {
      return (
        <div className="flex flex-wrap gap-3 items-stretch">
          <button
            type="button"
            onClick={onOpenForm}
            aria-label="You're invited"
            className={QUIET_STATUS_BTN}
            data-testid="invite-accepted-badge"
          >
            <UserPlus className="w-4 h-4 text-ink/50 shrink-0" strokeWidth={2} aria-hidden />
            <span className="font-ui font-medium text-[15px] text-ink">You&apos;re invited — you&apos;re in!</span>
          </button>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap gap-3 items-stretch">
        <Button
        variant="primary"
          size="lg"
          onClick={onOpenInviteForm}
          disabled={isFull}
          data-testid="request-invite-btn"
        >
          {isFull ? "Full — no spots left" : isPaid ? "Request invite (after paying) →" : "Request invite →"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-3 items-stretch">
      <Button
        variant="primary"
        size="lg"
        onClick={onOpenForm}
        disabled={isFull}
        className="inline-flex items-center justify-center"
        data-testid="open-rsvp-btn"
      >
        {isFull ? "Full — no spots left" : isNotConfirmed ? "RSVP anyway →" : "RSVP →"}
      </Button>
    </div>
  );
});

const EventDetailsPanel = memo(function EventDetailsPanel({
  defaultOpen,
  hosts,
  isRecurring,
  recurringProps,
  requiresInvite,
  isHosting,
  myRsvp,
  inviteStatus,
  isPaid,
  inviteCopy,
  showPayment,
  paymentProps,
}) {
  const hasHosts = hosts.length > 0;
  const hasRecurring = isRecurring;
  const hasInviteNotice = requiresInvite && !isHosting && !myRsvp && inviteStatus !== "accepted";

  if (!hasHosts && !hasRecurring && !hasInviteNotice && !showPayment) {
    return null;
  }

  return (
    <details
      className="max-w-page mx-auto px-4 mt-4 group"
      open={defaultOpen}
      data-testid="event-details-panel"
    >
      <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2 rounded-md2 border border-subtle bg-surface px-4 py-3 min-h-[44px] font-ui font-semibold text-[15px] text-ink focus-visible:outline-offset-[-2px]">
        <span>Event details</span>
        <ChevronDown className="w-5 h-5 text-ink transition-transform motion-reduce:transition-none group-open:rotate-180 shrink-0" strokeWidth={2.5} aria-hidden />
      </summary>
      <div className="mt-3 space-y-4">
        {hasInviteNotice && (
          <p className="font-ui text-[14px] font-medium text-ink/85 px-1" data-testid="invite-only-notice">
            {inviteCopy}
          </p>
        )}
        {showPayment && (
          <div data-testid="self-payment-section">
            <SelfPaymentDisplay {...paymentProps} />
          </div>
        )}
        {hasRecurring && (
          <div data-testid="recurring-info">
            <RecurringInfo {...recurringProps} />
          </div>
        )}
        {hasHosts && (
          <div data-testid="hosts-section">
            <p className="font-ui font-semibold text-[14px] text-ink/70 mb-2 flex items-center gap-1.5">
              <User2 className="w-3.5 h-3.5" strokeWidth={2.5} aria-hidden />
              {hosts.length > 1 ? "Hosts" : "Your host"}
            </p>
            <div className="space-y-2">
              {hosts.map((h) => (
                <HostCard key={`${h.role}-${h.organizer_id || h.name}`} host={h} />
              ))}
            </div>
          </div>
        )}
      </div>
    </details>
  );
});

export default function EventPage() {
  const { citySlug, eventSlug } = useParams();
  const [searchParams] = useSearchParams();
  const occurrenceDateParam = searchParams.get("occurrence_date");
  const location = useLocation();
  const nav = useNavigate();
  const embedded = Boolean(listingDrawerBackground(location));
  const { account } = useAuth();
  const { dancerId: guestDancerId, profile: guestProfile, bindDancerId } = useGuestIdentity();
  const [resolvedId, setResolvedId] = useState(null);
  const [pathResolving, setPathResolving] = useState(Boolean(citySlug && eventSlug));
  const [pathResolveError, setPathResolveError] = useState(null);
  const [pathResolveAttempt, setPathResolveAttempt] = useState(0);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [myRsvp, setMyRsvp] = useState(null);
  const [mySubscription, setMySubscription] = useState({ is_favourite: false, on_profile: false });
  const [loadError, setLoadError] = useState(false);
  const loadAbortRef = useRef(null);
  const dancerIdRef = useRef(guestDancerId);
  const eventSnapshotRef = useRef("");
  const heroRef = useRef(null);
  const [heroScrolledPast, setHeroScrolledPast] = useState(false);
  const [showFdSheet, setShowFdSheet] = useState(false);
  const [fdCanVote, setFdCanVote] = useState(false);
  const [guestListRefreshing, setGuestListRefreshing] = useState(false);
  const [favouritePromptOpen, setFavouritePromptOpen] = useState(false);
  const [favouritePromptBusy, setFavouritePromptBusy] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [pushPromptOpen, setPushPromptOpen] = useState(false);
  const [followAuthOpen, setFollowAuthOpen] = useState(false);
  const pendingFollowAfterAuthRef = useRef(false);

  useEffect(() => {
    dancerIdRef.current = guestDancerId;
  }, [guestDancerId]);

  // Intentional: mobile sticky RSVP stays hidden while the hero banner (with its primary
  // CTA) is visible; it slides in only after the user scrolls past the banner.
  useEffect(() => {
    const node = heroRef.current;
    if (!node || !event) {
      setHeroScrolledPast(false);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        const passed = !entry.isIntersecting;
        setHeroScrolledPast((prev) => (prev === passed ? prev : passed));
      },
      { threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [event?.id]);

  const comingRsvps = useMemo(
    () => (event?.rsvps ? sortComingRsvps(event.rsvps) : []),
    [event?.rsvps],
  );
  const tentativeRsvps = useMemo(
    () => (event?.rsvps ? event.rsvps.filter(isTentativeOrDeclined) : []),
    [event?.rsvps],
  );

  const openRsvpForm = useCallback(() => setShowForm(true), []);
  const closeRsvpForm = useCallback(() => setShowForm(false), []);
  const openInviteForm = openRsvpForm;

  const onShare = useCallback(() => {
    if (!event) return;
    openWhatsAppShare(buildEventWhatsAppSummary(event, { counts: event.counts }));
  }, [event]);

  const maybeOfferPushPrompt = useCallback(async () => {
    if (!guestDancerId || isPushPromptDismissed(guestDancerId)) return;
    if (await shouldOfferPush()) setPushPromptOpen(true);
  }, [guestDancerId]);

  const applyFavouriteCount = useCallback((favouriteCount, delta = 0) => {
    setEvent((prev) => {
      if (!prev) return prev;
      const nextCount = typeof favouriteCount === "number"
        ? favouriteCount
        : Math.max(0, (Number(prev.favourite_count) || 0) + delta);
      return { ...prev, favourite_count: nextCount };
    });
  }, []);

  const toggleFavourite = useCallback(async () => {
    if (!event?.id || !canFollowSeries(event)) return;
    if (!account) {
      pendingFollowAfterAuthRef.current = true;
      stashPendingAuthAction({ type: "follow_event", eventId: event.id });
      setFollowAuthOpen(true);
      return;
    }
    const next = !mySubscription.is_favourite;
    setFollowBusy(true);
    try {
      const body = {
        is_favourite: next,
        on_profile: mySubscription.on_profile,
      };
      if (guestDancerId) body.dancer_id = guestDancerId;
      const r = await api.patch(`/events/${event.id}/subscription`, body);
      if (r.data.dancer_id && r.data.dancer_id !== guestDancerId) {
        bindDancerId(r.data.dancer_id);
      }
      setMySubscription(r.data.subscription);
      applyFavouriteCount(r.data.favourite_count, next ? 1 : -1);
      toast.success(next ? "Following — we'll notify you about updates" : "Unfollowed");
      if (next) {
        const idForPush = r.data.dancer_id || guestDancerId;
        if (idForPush && !isPushPromptDismissed(idForPush) && await shouldOfferPush()) {
          setPushPromptOpen(true);
        }
      }
    } catch {
      toast.error("Couldn't update follow");
    } finally {
      setFollowBusy(false);
    }
  }, [
    account,
    guestDancerId,
    event,
    mySubscription.is_favourite,
    mySubscription.on_profile,
    applyFavouriteCount,
    bindDancerId,
  ]);

  const dismissFavouritePrompt = useCallback(async () => {
    if (!event?.id) {
      setFavouritePromptOpen(false);
      return;
    }
    if (!account) {
      setFavouritePromptOpen(false);
      return;
    }
    setFavouritePromptBusy(true);
    try {
      await api.patch(`/events/${event.id}/subscription`, {
        dancer_id: guestDancerId || undefined,
        favourite_prompt_dismissed: true,
      });
      setFavouritePromptOpen(false);
      maybeOfferPushPrompt();
    } catch {
      toast.error("Couldn't save preference");
    } finally {
      setFavouritePromptBusy(false);
    }
  }, [account, guestDancerId, event?.id, maybeOfferPushPrompt]);

  const starSeriesFromPrompt = useCallback(async () => {
    if (!event?.id) return;
    if (!account) {
      pendingFollowAfterAuthRef.current = true;
      stashPendingAuthAction({ type: "follow_event", eventId: event.id });
      setFavouritePromptOpen(false);
      setFollowAuthOpen(true);
      return;
    }
    setFavouritePromptBusy(true);
    try {
      const r = await api.patch(`/events/${event.id}/subscription`, {
        dancer_id: guestDancerId || undefined,
        is_favourite: true,
      });
      if (r.data.dancer_id && r.data.dancer_id !== guestDancerId) {
        bindDancerId(r.data.dancer_id);
      }
      setMySubscription(r.data.subscription);
      applyFavouriteCount(r.data.favourite_count, 1);
      setFavouritePromptOpen(false);
      toast.success("Following — we'll notify you about updates");
      maybeOfferPushPrompt();
    } catch {
      toast.error("Couldn't follow");
    } finally {
      setFavouritePromptBusy(false);
    }
  }, [account, guestDancerId, event?.id, applyFavouriteCount, maybeOfferPushPrompt, bindDancerId]);

  const onFollowAuthSuccess = useCallback(async ({ dancer_id: authDancerId } = {}) => {
    setFollowAuthOpen(false);
    if (authDancerId) bindDancerId(authDancerId);
    const hadPending = pendingFollowAfterAuthRef.current;
    pendingFollowAfterAuthRef.current = false;
    const action = consumePendingAuthAction("follow_event");
    const pending = hadPending
      || (action && String(action.eventId) === String(event?.id));
    if (!pending || !event?.id) return;
    setFollowBusy(true);
    try {
      const r = await api.patch(`/events/${event.id}/subscription`, {
        dancer_id: authDancerId || guestDancerId || undefined,
        is_favourite: true,
        on_profile: mySubscription.on_profile,
      });
      if (r.data.dancer_id) bindDancerId(r.data.dancer_id);
      setMySubscription(r.data.subscription);
      applyFavouriteCount(r.data.favourite_count, 1);
      toast.success("Following — we'll notify you about updates");
    } catch {
      toast.error("Couldn't follow");
    } finally {
      setFollowBusy(false);
    }
  }, [event?.id, guestDancerId, mySubscription.on_profile, applyFavouriteCount, bindDancerId]);

  useEffect(() => {
    if (!account || !event?.id) return;
    const action = consumePendingAuthAction("follow_event");
    if (!action || String(action.eventId) !== String(event.id)) return;
    if (mySubscription.is_favourite) return;
    let cancelled = false;
    (async () => {
      setFollowBusy(true);
      try {
        const r = await api.patch(`/events/${event.id}/subscription`, {
          dancer_id: guestDancerId || undefined,
          is_favourite: true,
          on_profile: mySubscription.on_profile,
        });
        if (cancelled) return;
        if (r.data.dancer_id) bindDancerId(r.data.dancer_id);
        setMySubscription(r.data.subscription);
        applyFavouriteCount(r.data.favourite_count, 1);
        toast.success("Following — we'll notify you about updates");
      } catch {
        if (!cancelled) toast.error("Couldn't follow");
      } finally {
        if (!cancelled) setFollowBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account, event?.id, guestDancerId, mySubscription.is_favourite, mySubscription.on_profile, applyFavouriteCount, bindDancerId]);

  const formattedStartsAt = useMemo(
    () => (event?.starts_at ? formatDate(event.starts_at) : ""),
    [event?.starts_at],
  );

  const earlyBirdLines = useMemo(
    () => (event?.pricing?.early_bird_active
      ? earlyBirdDetailLines(event.pricing, {
        rsvpCount: event.counts?.total,
        currencySymbol,
      })
      : []),
    [event?.pricing, event?.counts?.total],
  );

  const recurringPanelProps = useMemo(
    () => (event ? {
      pattern: event.recurring,
      nextIso: event.starts_at,
      recurringEnd: event.recurring_end,
      status: event.event_status,
    } : null),
    [event],
  );

  const paymentPanelProps = useMemo(
    () => (event ? {
      pricing: event.pricing,
      selfPayment: event.self_payment,
      organizerName: event.organizer_name,
    } : null),
    [event],
  );

  useEffect(() => {
    if (!citySlug || !eventSlug) {
      setPathResolving(false);
      setPathResolveError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setPathResolving(true);
    setPathResolveError(null);
    setResolvedId(null);
    setEvent(null);
    api
      .get(`/events/by-path/${encodeURIComponent(citySlug)}/${encodeURIComponent(eventSlug)}`)
      .then((r) => {
        if (!cancelled) {
          setResolvedId(r.data.id);
          setPathResolveError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setResolvedId(null);
          setEvent(null);
          setLoading(false);
          const status = e?.response?.status;
          setPathResolveError(status === 404 ? "not_found" : "error");
        }
      })
      .finally(() => {
        if (!cancelled) setPathResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [citySlug, eventSlug, pathResolveAttempt]);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!resolvedId) return;

    loadAbortRef.current?.abort();
    const controller = new AbortController();
    loadAbortRef.current = controller;

    try {
      let dancerId = dancerIdRef.current || guestDancerId || null;

      if (!background) {
        // Skip waiting on profile when we already have a usable dancer id (local or preloaded).
        const hasProfile = Boolean(guestProfile?.id);
        const hasKnownId = Boolean(dancerId);
        if (hasProfile) {
          dancerId = guestProfile.id;
          dancerIdRef.current = guestProfile.id;
          if (guestProfile.id !== guestDancerId) bindDancerId(guestProfile.id);
        } else if (!hasKnownId) {
          const profile = await loadDancerProfile({
            dancerId: guestDancerId,
            account,
          });
          dancerId = profile?.id || guestDancerId;
          if (profile?.id) {
            dancerIdRef.current = profile.id;
            if (profile.id !== guestDancerId) bindDancerId(profile.id);
          }
        } else {
          // Known id: refresh profile in the background; event GET proceeds immediately.
          loadDancerProfile({ dancerId: guestDancerId, account }).then((profile) => {
            if (!profile?.id || controller.signal.aborted) return;
            dancerIdRef.current = profile.id;
            if (profile.id !== guestDancerId) bindDancerId(profile.id);
          }).catch(() => {});
        }
      }

      const r = await api.get(`/events/${resolvedId}`, {
        params: {
          dancer_id: ownDancerIdParam(dancerId),
          occurrence_date: occurrenceDateParam || undefined,
        },
        signal: controller.signal,
      });

      if (controller.signal.aborted) return;

      const signature = eventPollSignature(r.data);
      const guestListChanged = signature !== eventSnapshotRef.current;

      if (background && !guestListChanged) return;

      setLoadError(false);

      if (r.data.dancer_id && r.data.dancer_id !== guestDancerId) {
        bindDancerId(r.data.dancer_id);
        dancerId = r.data.dancer_id;
        dancerIdRef.current = r.data.dancer_id;
      }

      const mine =
        r.data.my_rsvp ||
        (dancerId
          ? (r.data.rsvps || []).find((x) => x.dancer_id === dancerId)
          : null);
      setMyRsvp(mine || null);
      if (mine?.checked_in_at) {
        setActiveCheckIn({
          event_id: resolvedId,
          occurrence_date: mine.occurrence_date,
          checked_in_at: mine.checked_in_at,
          public_path: r.data.public_path,
        });
      }
      setMySubscription(r.data.my_subscription || { is_favourite: false, on_profile: false });

      if (!background || guestListChanged) {
        eventSnapshotRef.current = signature;
        setEvent(r.data);
      }
    } catch (e) {
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") return;
      if (background) {
        setLoadError(true);
      } else {
        setEvent(null);
      }
    } finally {
      if (!background && !controller.signal.aborted) setLoading(false);
    }
  }, [resolvedId, account, guestDancerId, guestProfile, bindDancerId, occurrenceDateParam]);


  useEffect(() => {
    const occurrenceDate = event?.selected_occurrence || myRsvp?.occurrence_date;
    if (!event?.id || !guestDancerId || !myRsvp?.checked_in_at) {
      setFdCanVote(false);
      return;
    }
    if (!isOccurrenceWithinEventVoteRange(occurrenceDate)) {
      setFdCanVote(false);
      return;
    }
    let cancelled = false;
    fetchFavoriteDances(event.id, {
      dancerId: guestDancerId,
      occurrenceDate,
    })
      .then((data) => {
        if (!cancelled) {
          setFdCanVote(Boolean(data.vote_window_open && !data.submitted));
        }
      })
      .catch(() => { if (!cancelled) setFdCanVote(false); });
    return () => { cancelled = true; };
  }, [event?.id, event?.selected_occurrence, guestDancerId, myRsvp?.checked_in_at, myRsvp?.occurrence_date]);

  const refreshEvent = useCallback(() => load({ background: true }), [load]);

  const refreshGuestList = useCallback(async () => {
    setGuestListRefreshing(true);
    try {
      await load({ background: true });
    } finally {
      setGuestListRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    if (!resolvedId) return;
    eventSnapshotRef.current = "";
    setLoading(true);
    load({ background: false });
    return () => {
      loadAbortRef.current?.abort();
    };
  }, [resolvedId, load]);

  if (pathResolveError === "error") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-canvas p-8 text-center">
        <Card className="w-full max-w-md mx-auto p-6 sm:p-8">
          <p className="font-ui font-semibold text-[28px] leading-tight tracking-tight">
            Could not load this event
          </p>
          <p className="font-ui text-[15px] text-ink/70 mt-2">
            The server may be starting up. Try again in a moment.
          </p>
          <div className="mt-6 flex flex-col items-stretch gap-3">
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                setLoading(true);
                setPathResolveAttempt((n) => n + 1);
              }}
            >
              Try again
            </Button>
            <Button
        variant="secondary" as={Link} to="/">
              Go to home
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  if (pathResolveError === "not_found" || (!pathResolving && !loading && !event)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-canvas p-8 text-center">
        <Card className="w-full max-w-md mx-auto p-6 sm:p-8" data-testid="event-inactive-card">
          <p className="font-ui font-semibold text-[28px] leading-tight tracking-tight">
            This event is no longer active
          </p>
          <p className="font-ui text-[15px] text-ink/70 mt-2">
            It may have been deleted or the link is out of date.
          </p>
          <Button as={Link} to="/" variant="primary" className="mt-6 w-full" data-testid="event-inactive-home-btn">
            Go to home
          </Button>
        </Card>
      </div>
    );
  }
  if (pathResolving || loading) {
    return <EventPageLoadingSkeleton />;
  }

  const counts = event.counts;
  const cap = event.capacity;
  const isCancelled = event.event_status === "cancelled";
  const isNotConfirmed = event.event_status === "not_confirmed";
  const isFull = cap && counts.total >= cap;
  const mapLink = eventMapLink(event);
  const mapLabel = eventMapQuery(event);
  const myStatus = myRsvp ? attendanceStatus(myRsvp) : null;
  const isHosting = Boolean(event.is_hosting);
  const hosts = event.hosts || [];
  const ownerTheme = normalizeHostTheme(hosts[0]?.theme || event.theme || DEFAULT_HOST_THEME);
  const isRecurring = canFollowSeries(event);
  const showFollowInMenu = isRecurring;
  const requiresInvite = eventRequiresInvite(event);
  const myInviteRequest = event.my_invite_request || null;
  const inviteStatus = myInviteRequest?.status || null;
  const isPaid = event.pricing?.type && event.pricing.type !== "free";
  const pendingInvites = isHosting ? (event.invite_requests || []).length : 0;
  const showCheckIn = canOfferCheckIn({
    event,
    myRsvp,
    activeCheckIn: getActiveCheckInForSession(guestDancerId),
  });
  const floorPath = myRsvp?.checked_in_at
    ? activeCheckInPath({
      event_id: event.id,
      occurrence_date: event.selected_occurrence || myRsvp.occurrence_date,
      public_path: event.public_path,
    })
    : null;

  const showPayment = event.pricing?.type === "paid-self-managed" && event.self_payment && hasPaymentMethod(event.self_payment);
  const detailsOpenByDefault = !!(showPayment || (requiresInvite && !myRsvp && !isHosting));
  const inviteCopy = isPaid
    ? event.pricing?.type === "paid-self-managed"
      ? "Paid, invite-only — pay below, then request an invite. Host confirms once payment is received."
      : "Paid, invite-only — request an invite; host confirms once you've paid."
    : "Invite-only — request an invite and your host adds you to the list.";

  const stickyCta = getStickyCtaState({
    isCancelled,
    myRsvp,
    requiresInvite,
    isHosting,
    inviteStatus,
    isFull,
    isNotConfirmed,
  });
  const showStickyFooter = stickyCta && heroScrolledPast;

  return (
    <HostThemedShell
      theme={ownerTheme}
      syncDocument={!embedded}
      className={`event-page bg-canvas font-body text-ink ${embedded ? "min-h-full" : "min-h-screen"} ${showStickyFooter ? "pb-24 sm:pb-6" : "pb-6"}`}
    >
      {/* Top bar — back + host dashboard + overflow actions */}
      <DetailPageNav
        className={embedded ? "pt-3" : undefined}
        back={embedded ? (
          <Button
        variant="secondary"
            type="button"
            onClick={() => nav(-1)}
            data-testid="event-drawer-back"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            Back
          </Button>
        ) : (
          <Button
        variant="secondary"
            type="button"
            onClick={() => goBack(nav, location, [defaultBackPath(account)])}
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
            {backLabel(location, account ? defaultBackLabel(account) : "Rsvp.Dance")}
          </Button>
        )}
        actions={(
          <>
            {isHosting && (
              <Button
        variant="secondary"
                as={Link}
                to={`/dashboard/${event.id}`}
                className="bg-purply border-transparent"
                data-testid="event-dashboard-link"
              >
                <BarChart3 className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                Dashboard
                <CountBadge count={pendingInvites} className="ml-0.5" />
              </Button>
            )}
            <EventActionMenu
              editHref={isHosting ? eventEditPath(event) : null}
              showEditRsvp={Boolean(myRsvp)}
              onEditRsvp={openRsvpForm}
              showFollow={showFollowInMenu}
              isFavourite={mySubscription.is_favourite}
              onFavourite={toggleFavourite}
              onShare={onShare}
            />
          </>
        )}
      />

      {loadError && (
        <section className="max-w-page mx-auto px-4 pt-3" data-testid="event-refresh-error">
          <Card className="p-3 flex items-center justify-between gap-3 bg-surface-muted">
            <p className="font-ui text-[14px] font-medium text-ink/80">
              Couldn&apos;t refresh the guest list.
            </p>
            <Button
        variant="secondary"
              type="button"
              onClick={refreshGuestList}
              className="shrink-0 text-[13px]"
            >
              Retry
            </Button>
          </Card>
        </section>
      )}

      {event.lifecycle === "archived" && isHosting && (
        <section className="max-w-page mx-auto px-4 pt-3" data-testid="archived-event-banner">
          <Card className="p-4">
            <p className="font-ui font-semibold text-[15px]">
              This event is archived — guests can&apos;t RSVP. Restore it from the dashboard.
            </p>
          </Card>
        </section>
      )}


      {event.favorite_dances_aggregate?.message && (
        <section className="max-w-page mx-auto px-4 pt-3" data-testid="favorite-dances-aggregate">
          <Card className="p-4 border-black/[0.04]">
            <p className="font-ui font-medium text-[14px] text-ink/70">{event.favorite_dances_aggregate.message}</p>
          </Card>
        </section>
      )}

      {/* Hero — cover + quiet body (Lu.ma rhythm) */}
      <DetailHeroCard
        ref={heroRef}
        color={event.cover_color}
        testId="event-hero"
        titleTestId="event-name"
        title={event.name}
        contentMuted={requiresInvite && !isHosting && inviteStatus === "rejected"}
        eyebrow={(
          <p className="font-ui text-[12px] font-medium text-white/75 break-words min-w-0">
            {hosts.length > 1 ? "Hosted by multiple hosts" : `Hosted by ${event.organizer_name}`}
          </p>
        )}
        aside={<PriceHeroPill pricing={event.pricing} data-testid="pricing-pill" />}
        meta={(
          <>
            <DetailMetaRow icon={Calendar}>{formattedStartsAt}</DetailMetaRow>
            {mapLink ? (
              <DetailMetaRow icon={MapPin} href={mapLink} external testId="open-map-link">
                {mapLabel || event.location}
              </DetailMetaRow>
            ) : (
              <DetailMetaRow icon={MapPin}>{mapLabel || event.location}</DetailMetaRow>
            )}
            {event.instagram_url ? (
              <DetailMetaRow icon={Instagram} href={instagramHref(event.instagram_url)} external>
                Instagram
              </DetailMetaRow>
            ) : null}
          </>
        )}
        cta={(
          <HeroPrimaryCta
            isCancelled={isCancelled}
            myRsvp={myRsvp}
            myStatus={myStatus}
            requiresInvite={requiresInvite}
            isHosting={isHosting}
            inviteStatus={inviteStatus}
            isFull={isFull}
            isPaid={isPaid}
            isNotConfirmed={isNotConfirmed}
            showCheckIn={showCheckIn}
            onOpenForm={openRsvpForm}
            onOpenInviteForm={openInviteForm}
          />
        )}
      >
        {event.description && (
          <p className="text-[14px] font-normal text-ink/70 leading-relaxed whitespace-pre-line" data-testid="event-description">
            <LinkifiedText linkClassName="underline underline-offset-2 text-ink hover:text-ink/70">
              {event.description}
            </LinkifiedText>
          </p>
        )}
        {event.pricing?.early_bird_active && earlyBirdLines.length > 0 && (
          <div className="rounded-btn px-3 py-2 bg-canvas border border-black/[0.04]" data-testid="early-bird-banner">
            {earlyBirdLines.map((line) => (
              <p key={line} className="font-ui text-[13px] font-medium leading-snug text-ink/75">{line}</p>
            ))}
          </div>
        )}
        {(isCancelled || isNotConfirmed) && (
          <div
            className={`rounded-btn px-3 py-2.5 border ${
              isCancelled
                ? "bg-night text-cream border-night"
                : "bg-canvas text-ink border-black/[0.06]"
            }`}
            data-testid="event-status-banner"
          >
            <p className="font-ui font-medium text-[14px]">
              {isCancelled ? "This week's event is cancelled" : "Not confirmed yet"}
            </p>
            {event.status_reason && (
              <p className={`mt-1 text-[13px] font-normal ${isCancelled ? "text-cream/80" : "text-ink/65"}`}>
                <LinkifiedText
                  linkClassName={`underline underline-offset-2 hover:opacity-80 ${
                    isCancelled ? "text-cream hover:text-cream/80" : "text-ink hover:text-ink/70"
                  }`}
                >
                  {event.status_reason}
                </LinkifiedText>
              </p>
            )}
          </div>
        )}
      </DetailHeroCard>

      {(event.host_school || (isHosting && !event.host_school_opt_out)) && (
        <div className="max-w-page mx-auto px-4 pt-4">
          <HostSchoolPromo
            hostSchool={event.host_school}
            hostSchoolOptOut={Boolean(event.host_school_opt_out)}
            isHosting={isHosting}
            event={event}
            eventName={event.name}
          />
        </div>
      )}

      {showCheckIn && (
        <section className="max-w-page mx-auto px-4 pt-4" data-testid="check-in-section">
          <CheckInButton
            eventId={event.id}
            occurrenceDate={event.selected_occurrence || myRsvp.occurrence_date}
            publicPath={event.public_path}
            checkedInAt={myRsvp.checked_in_at}
            onCheckedIn={refreshEvent}
            color="amber"
            size="lg"
            className="w-full"
            testId="event-check-in-btn"
          />
        </section>
      )}

      {/* Guest list — primary content, immediately after hero */}
      <section id="guest-list" className="max-w-page mx-auto px-4 pt-5 pb-4 scroll-mt-4 [contain:layout_style]">
        <GuestListPullToRefresh onRefresh={refreshGuestList} refreshing={guestListRefreshing}>
          <EventGuestList
            comingRsvps={comingRsvps}
            tentativeRsvps={tentativeRsvps}
            counts={counts}
            cap={cap}
            showFollow={isRecurring && !isCancelled}
            followerCount={event.favourite_count || 0}
            isFollowing={mySubscription.is_favourite}
            followBusy={followBusy}
            onToggleFollow={toggleFavourite}
            isNotConfirmed={isNotConfirmed}
          />
        </GuestListPullToRefresh>
      </section>

      <GuestListRefreshingPopup open={guestListRefreshing} />


      {fdCanVote && (
        <section className="max-w-page mx-auto px-4 mt-3" data-testid="favorite-dances-entry">
          <Button
        variant="primary"
            className="w-full"
            onClick={() => setShowFdSheet(true)}
            aria-label={FD_VOTE_TITLE}
          >
            {FD_EVENT_CTA}
          </Button>
        </section>
      )}

      {floorPath && (
        <section className="max-w-page mx-auto px-4 mt-3" data-testid="floor-link-section">
          <Button
        variant="secondary"
            as={Link}
            to={floorPath}
            state={{ from: `${location.pathname}${location.search}` }}
            className="w-full py-3.5 font-ui font-semibold text-[15px] bg-amber border-amber hover:bg-amber/90"
            data-testid="event-floor-link"
          >
            Back to the floor
          </Button>
        </section>
      )}

      <EventDetailsPanel
        defaultOpen={detailsOpenByDefault}
        hosts={hosts}
        isRecurring={isRecurring}
        recurringProps={recurringPanelProps}
        requiresInvite={requiresInvite}
        isHosting={isHosting}
        myRsvp={myRsvp}
        inviteStatus={inviteStatus}
        isPaid={isPaid}
        inviteCopy={inviteCopy}
        showPayment={showPayment}
        paymentProps={paymentPanelProps}
      />

      {/* Mobile sticky RSVP — intentionally hidden until hero scrolls away (see heroScrolledPast). */}

      {showFdSheet && event?.id && guestDancerId && (
        <FavoriteDancesSheet
          open
          eventId={event.id}
          occurrenceDate={event.selected_occurrence || myRsvp?.occurrence_date}
          dancerId={guestDancerId}
          onClose={() => setShowFdSheet(false)}
          onSubmitted={() => {
            setShowFdSheet(false);
            setFdCanVote(false);
            refreshEvent();
          }}
        />
      )}

      <EventStickyCta
        cta={stickyCta}
        visible={heroScrolledPast}
        onOpenForm={openRsvpForm}
        onOpenInviteForm={openInviteForm}
        requiresInvite={requiresInvite && !isHosting}
      />

      {/* ===== RSVP / INVITE MODAL ===== */}
      {showForm && (
        <Suspense fallback={null}>
          <RsvpModal
            event={event}
            existingRsvp={myRsvp}
            subscription={mySubscription}
            onClose={closeRsvpForm}
            onDone={(d, opts = {}) => {
              const { close = true, toast: showToast = true, subscription, showFavouritePrompt } = opts;
              bindDancerId(d.dancer_id);
              if (subscription) setMySubscription(subscription);
              if (d.invite_requested) {
                if (showToast) {
                  toast.success("Invite requested — your host will review it soon.");
                }
                load();
                if (close) closeRsvpForm();
                return;
              }
              if (showToast) {
                const msg =
                  d.attendance_status === "not_coming"
                    ? "Thanks for letting us know — hope to see you next time!"
                    : d.attendance_status === "maybe"
                      ? "Saved — we'll hope to see you!"
                      : "You're in! See you on the floor.";
                toast.success(msg);
              }
              load();
              if (close) closeRsvpForm();
              if (showFavouritePrompt && event?.recurring && event.recurring !== "none") {
                setFavouritePromptOpen(true);
              } else if (d.attendance_status === "likely" || d.attendance_status === "maybe") {
                maybeOfferPushPrompt();
              }
            }}
          />
        </Suspense>
      )}

      <FavouriteSeriesPromptDialog
        open={favouritePromptOpen}
        eventName={event?.name}
        busy={favouritePromptBusy}
        onStar={starSeriesFromPrompt}
        onDismiss={dismissFavouritePrompt}
      />

      <PushNotificationPromptDialog
        open={pushPromptOpen}
        dancerId={guestDancerId}
        onClose={() => setPushPromptOpen(false)}
      />

      <AuthModal
        open={followAuthOpen}
        onClose={() => {
          pendingFollowAfterAuthRef.current = false;
          setFollowAuthOpen(false);
        }}
        onSuccess={onFollowAuthSuccess}
        title="Sign in to follow this series"
        subtitle="Following saves updates to your account so they show up in Your Events after you come back."
      />
    </HostThemedShell>
  );
}

// ============================================================
// Self-managed payment info
// ============================================================
// Recurring info — shown on guest event page (no date picker).
// ============================================================
const PATTERN_LABELS = {
  weekly: { adj: "weekly", every: "every week" },
  biweekly: { adj: "bi-weekly", every: "every two weeks" },
  monthly: { adj: "monthly", every: "every month" },
};

function RecurringInfo({ pattern, nextIso, recurringEnd, status }) {
  const labels = PATTERN_LABELS[pattern] || { adj: pattern, every: "regularly" };
  const nextDate = nextIso
    ? new Date(nextIso).toLocaleString("en-GB", {
        weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
      })
    : null;
  const endDate = recurringEnd
    ? new Date(recurringEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <Card className="p-4 sm:p-5 bg-surface-muted">
      <div className="flex items-start gap-3">
        <span className="rounded-md2 border border-subtle bg-surface p-2 shrink-0 text-ink">
          <Repeat className="w-5 h-5" strokeWidth={2.5} aria-hidden />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-ui text-[12px] font-medium text-ink/70">
            Recurring · {labels.adj}
          </p>
          <p className="font-ui font-semibold text-[15px] sm:text-[16px] leading-snug mt-0.5">
            Happens {labels.every}. You&apos;re RSVPing for the next one:
          </p>
          {nextDate && (
            <p className="font-ui font-semibold text-[16px] sm:text-[17px] mt-1" data-testid="recurring-next-date">
              {nextDate}
            </p>
          )}
          {status && status !== "confirmed" && (
            <Badge color={eventStatusColor(status)} className="mt-2">
              {eventStatusLabel(status)}
            </Badge>
          )}
          {endDate && (
            <p className="text-[12px] font-ui font-medium text-ink/70 mt-1">
              Series runs until {endDate}.
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
